// Analytic solar geometry for the Analysis-Mode heatmap.
// Pure math (no three.js) so it stays testable. Returns sun directions as [x, y, z]
// in the viewport's coordinate frame: Y = up, +Z = South, +X = East, -Z = North
// (matches Viewport3D's classifyFace convention).

const rad = (d) => (d * Math.PI) / 180;
const deg = (r) => (r * 180) / Math.PI;

// Solar declination per season (deg). Equinoxes ≈ 0°, solstices ≈ ±23.44°.
export const SEASON_DECL = { SPR: 0, SUM: 23.44, AUT: 0, WNT: -23.44 };
export const SEASON_LABELS = { SPR: "Spring", SUM: "Summer", AUT: "Autumn", WNT: "Winter" };

// Solar altitude + azimuth for a latitude, declination and hour angle.
// northOffsetDeg rotates the azimuth to match the model's true north.
export function solarAltAz(latDeg, declDeg, hourDeg, northOffsetDeg = 0) {
  const lat = rad(latDeg);
  const decl = rad(declDeg);
  const h = rad(hourDeg);
  const sinAlt = Math.sin(lat) * Math.sin(decl) + Math.cos(lat) * Math.cos(decl) * Math.cos(h);
  const alt = Math.asin(Math.max(-1, Math.min(1, sinAlt)));
  const cosAlt = Math.cos(alt);
  let cosAz = (Math.sin(decl) - Math.sin(lat) * Math.sin(alt)) / (Math.cos(lat) * cosAlt || 1e-6);
  cosAz = Math.max(-1, Math.min(1, cosAz));
  let az = Math.acos(cosAz); // azimuth measured from North (0), going clockwise toward East
  if (h > 0) az = 2 * Math.PI - az; // afternoon → western sky
  az += rad(northOffsetDeg);
  return { alt, az, cosAlt };
}

// Sun direction (pointing FROM the surface TOWARD the sun). Returns null when the sun is
// at/below the horizon.
export function sunDirection(latDeg, declDeg, hourDeg, northOffsetDeg = 0) {
  const { alt, az, cosAlt } = solarAltAz(latDeg, declDeg, hourDeg, northOffsetDeg);
  if (alt <= rad(2)) return null; // below (or grazing) the horizon → no useful light
  return [cosAlt * Math.sin(az), Math.sin(alt), -cosAlt * Math.cos(az)];
}

// Sunrise/sunset hour angle H0 (deg): cos H0 = -tan(lat)·tan(decl).
// Returns 180 for the midnight sun (never sets) and 0 for polar night (never rises),
// so daylight length automatically shrinks in winter / grows in summer with latitude.
export function daylightHourAngle(latDeg, declDeg) {
  const x = -Math.tan(rad(latDeg)) * Math.tan(rad(declDeg));
  if (x <= -1) return 180; // midnight sun
  if (x >= 1) return 0; // polar night
  return deg(Math.acos(x));
}

// Sun samples across the TRUE daylight window for this site/season, each weighted by the
// clear-sky beam intensity on a horizontal plane (sin altitude ≈ air-mass-free irradiance).
// Longer summer days accumulate more weighted samples than short winter days → the baked
// exposure reflects real seasonal insolation rather than a fixed noon snapshot.
// Returns [{ d: [x, y, z], w }].
export function daySunSamples(latDeg, season, northOffsetDeg = 0, steps = 9) {
  const decl = SEASON_DECL[season] ?? 23.44;
  const H0 = daylightHourAngle(latDeg, decl);
  const out = [];
  if (H0 <= 0 || steps < 2) return out; // polar night → no sun
  for (let i = 0; i < steps; i++) {
    const hourDeg = -H0 + (2 * H0 * i) / (steps - 1);
    const { alt, az, cosAlt } = solarAltAz(latDeg, decl, hourDeg, northOffsetDeg);
    if (alt <= rad(2)) continue;
    out.push({ d: [cosAlt * Math.sin(az), Math.sin(alt), -cosAlt * Math.cos(az)], w: Math.sin(alt) });
  }
  return out;
}

// Back-compat: plain (unweighted) direction list across the daylight window.
export function daySunDirections(latDeg, season, northOffsetDeg = 0) {
  return daySunSamples(latDeg, season, northOffsetDeg).map((s) => s.d);
}
